// Shared staging-review slide-over. Extracted from CollectionStagingQueuePage
// so the same drawer (file viewer, AI distillation, scan, reviewer note,
// approve / file / return) can be opened from every in-place host (the
// cross-account Review Queue, the Broker detail → Review Queue tab, the
// package detail page, the loan Financial Reporting tab, …) without
// navigating away. Every host renders this identically; each owns its own
// open/close state and passes the clicked queue row for the subtitle + due-date
// editor. The header's "Open in full queue" link is the one deliberate,
// explicit escape hatch into /collection/staging — hosts should never add
// their own row-level link to the queue (that was the stranding bug this
// drawer replaced).

import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { PackageDueDateEditor } from "@/components/Collection/PackageDueDateEditor";
import { StagedSubmissionReviewPanel } from "@/components/Collection/StagedSubmissionReviewPanel";
import { PackageRequestView } from "@/components/Collection/PackageRequestView";
import type { StagedSubmissionListItem } from "@/api/collectionStaging";
import type { CollectionPackageQueueItem } from "@/api/collectionPackages";

interface StagedSubmissionReviewDrawerProps {
  /** The staged submission to review; when null the drawer is in
   *  package-view mode (see packageRow) or closed. */
  submissionId: string | null;
  /**
   * The queue row backing the open submission, used for the drawer subtitle
   * and the inline due-date editor. May be null when the row is no longer in
   * the caller's list (the panel still loads the submission by id).
   */
  row: StagedSubmissionListItem | null;
  /**
   * F8 — package-view mode. When there is no submission (a pending-recipient
   * request), open the SAME slide-out on the package instead, showing what was
   * sent + where each item stands (see PackageRequestView). Mutually exclusive
   * with submissionId (submission mode wins); requires accountId. Default null:
   * every existing call site renders the submission-review path unchanged.
   */
  packageRow?: CollectionPackageQueueItem | null;
  /** F8 — the portal account backing packageRow (for the account-scoped,
   *  IDOR-guarded item read). Only used in package-view mode. */
  accountId?: string | null;
  /** Close the drawer. Also fires when the panel completes a terminal action. */
  onClose: () => void;
  /**
   * §2.4 entity-surface mounts — suppress the header "Open in full queue"
   * escape hatch so review never leaves the entity page. Default falsy:
   * /collection/staging and every existing host render unchanged.
   */
  hideFullQueueLink?: boolean;
  /** §2.4 — threaded to the panel; hides Manage Assignees / Change owner. */
  hideQueueActions?: boolean;
}

export function StagedSubmissionReviewDrawer({
  submissionId,
  row,
  packageRow = null,
  accountId = null,
  onClose,
  hideFullQueueLink = false,
  hideQueueActions = false,
}: StagedSubmissionReviewDrawerProps) {
  // F8 — package-view mode: a pending-recipient request has no staged
  // submission yet, so show the package's contents + status in the same
  // slide-out. Submission mode takes precedence when both are supplied.
  if (!submissionId && packageRow) {
    return (
      <ResourceDrawer
        open
        width="xl"
        onClose={onClose}
        dismissOnBackdropClick={false}
        title="Document request"
        subtitle={`${packageRow.recipientFullName} · ${packageRow.itemsCompleted}/${packageRow.itemsTotal} items`}
        testId="package-request-drawer"
      >
        <PackageRequestView
          accountId={accountId ?? ""}
          pkg={packageRow}
          onPackageGone={onClose}
        />
      </ResourceDrawer>
    );
  }

  if (!submissionId) return null;
  return (
    <ResourceDrawer
      open
      width="xl"
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Review submission"
      subtitle={row ? `${row.templateName} · ${row.recipientName}` : undefined}
      testId="staging-review-drawer"
      headerActions={
        hideFullQueueLink ? undefined : (
          <Link
            to={`/collection/staging?submissionId=${submissionId}`}
            className="inline-flex items-center gap-space-1 text-caption text-gray-mid hover:text-gold-shadow whitespace-nowrap"
          >
            Open in full queue
            <ArrowRight size={14} aria-hidden />
          </Link>
        )
      }
    >
      {row && (
        <div className="mb-space-4">
          <PackageDueDateEditor
            packageId={row.packageId}
            dueDate={row.packageDueDate}
          />
        </div>
      )}
      <StagedSubmissionReviewPanel
        key={submissionId}
        id={submissionId}
        onComplete={() => onClose()}
        hideQueueActions={hideQueueActions}
      />
    </ResourceDrawer>
  );
}
